import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { PenLine } from "lucide-react";

import JobHomeWidget from "@/components/JobHomeWidget";
import PhotoDocket from "@/components/capture/PhotoDocket";
import VoiceRecorder from "@/components/capture/VoiceRecorder";
import RecentDockets from "@/components/capture/RecentDockets";
import SupplierQuickPick from "@/components/capture/SupplierQuickPick";

/**
 * Capture — lean action-first surface. Order (top → bottom):
 *
 *   header  (Layout.jsx: hard hat + BUILDINTEL AI + "Accounting in your
 *            pocket." + job chip + avatar)
 *   ├─ single 20px slogan     "Snap it. Speak it. Done."   (orange "Done.")
 *   ├─ Photo Receipt          (primary orange card)
 *   ├─ Voice Note + Manual    (equal stacked rows)
 *   ├─ Coach row              (48px, orange sparkle → opens ChatFab)
 *   ├─ Twin quick-pick chips
 *   ├─ Recent receipts
 *   └─ Active Job widget      (LAST, full Reno-style; hidden if no job)
 *
 * The coach row dispatches `buildintel:open-chat` — ChatFab listens for
 * that event and opens its sheet, so this stays a plain button (no
 * duplicated sheet, no TTS tile). FAB itself is unchanged.
 */

function useRecentEntries() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api
      .get("/entries")
      .then(({ data }) => setEntries(data.slice(0, 20)))
      .catch(() => {});
  }, []);
  return entries;
}

function useSupplierPatterns() {
  const [patterns, setPatterns] = useState([]);
  useEffect(() => {
    api
      .get("/suppliers/patterns")
      .then(({ data }) => setPatterns(data.patterns || []))
      .catch(() => {});
  }, []);
  return patterns;
}

export default function Capture() {
  const nav = useNavigate();
  const { activeJobId, jobs } = useApp();
  const activeJob = jobs.find((j) => j.id === activeJobId);
  const recentEntries = useRecentEntries();
  const patterns = useSupplierPatterns();

  const supplierSuggestions = useMemo(
    () => patterns.slice(0, 6).map((p) => p.supplier),
    [patterns]
  );

  const goManual = (prefill = {}) =>
    nav("/review", {
      state: {
        parsed: { source: "manual", job_id: activeJobId, ...prefill },
        source: "manual",
        supplierSuggestions,
      },
    });

  return (
    <div className="space-y-4" data-testid="capture-root">
      {/* Slogan — one line above Photo. Orange "Done." only; no subtitle,
          no AI READY badge. 20px = text-xl (`clamp` handles 375px width). */}
      <div
        data-testid="capture-slogan"
        className="text-[20px] leading-tight font-heading font-semibold text-white pt-1"
      >
        Snap it. Speak it. <span className="text-[#FF9500]">Done.</span>
      </div>

      {/* Quiet fallback when the tradie hasn't picked an active job yet.
          Kept subtle — never a gold banner. */}
      {!activeJob && (
        <p
          data-testid="capture-no-job-hint"
          className="text-[13px] text-white/50 -mt-1"
        >
          Add a job to tag spend.
        </p>
      )}

      {/* ─── PRIMARY ACTION ─────────────────────────────────────────── */}
      <PhotoDocket
        activeJobId={activeJobId}
        jobs={jobs}
        supplierSuggestions={supplierSuggestions}
      />

      {/* ─── SECONDARY ACTIONS · equal stacked rows ─────────────────── */}
      <div className="grid grid-cols-1 gap-4" data-testid="capture-secondary">
        <VoiceRecorder
          activeJobId={activeJobId}
          supplierSuggestions={supplierSuggestions}
        />
        <button
          type="button"
          data-testid="manual-entry-btn"
          onClick={() => goManual()}
          className="group h-20 w-full rounded-2xl bi-elevate flex items-center px-4 gap-4 hover:brightness-[1.08] active:scale-[0.99] transition"
        >
          <div className="w-12 h-12 rounded-xl bg-[#FF9500]/15 flex items-center justify-center text-[#FF9500] group-hover:bg-[#FF9500]/25 group-active:scale-95 transition">
            <PenLine className="w-5 h-5" />
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="font-heading font-semibold text-[17px] leading-tight">
              Manual Entry
            </div>
            <div className="text-[13px] text-white/60 leading-snug">
              Type it in the old fashioned way.
            </div>
          </div>
        </button>
      </div>

      {/* Coach row removed — coach lives solely on the ChatFab now. */}

      {/* ─── TWIN CHIPS ─────────────────────────────────────────────── */}
      <SupplierQuickPick
        suppliers={supplierSuggestions}
        onPick={(s) => goManual({ supplier: s })}
      />

      {/* ─── RECENT RECEIPTS ────────────────────────────────────────── */}
      <RecentDockets entries={recentEntries} />

      {/* ─── ACTIVE JOB (full widget, last) ─────────────────────────── */}
      {/* Full Reno-style card: spend / bar / category split / site docs.
          Hidden entirely when no job is selected. Uniform padding from
          `--content-pad-bottom` (index.css) keeps the FAB clear. */}
      {activeJob && <JobHomeWidget />}
    </div>
  );
}
